/**
 * IMPORTS
 */
import initialState from 'src/aggregates/ai/initialstate';
import Store from 'src/store';


/**
 * TYPES
 */
import * as Events from 'src/aggregates/ai/events.d';
import {IAiReducer} from 'src/aggregates/ai/reducer.d';
import {IAiState} from 'src/aggregates/ai/state.d';


/**
 * CODE
 */

// AI actions map
const actionsMap: IAiReducer = {

    /**
     * I save updated agent in state after add unanswered message to faq.
     *
     * :param state: ai state
     * :param event: ai-agents-add-to-training event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_ADDED_TO_TRAINING]: (
        state: IAiState,
        event: Events.IAiAgentsAddedToTraining,
    ): IAiState => ({
        ...state,
        agents: {
            ...state.agents,
            [event._target]: {
                ...state.agents[event._target],
                ...event.aiAgent,
            },
        },
        hasAddToTrainingError: false,
        isAddingToTraining: false,
    }),


    /**
     * I set loading state on ai-agents-adding-to-training event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_ADDING_TO_TRAINING]: (
        state: IAiState,
    ): IAiState => ({
        ...state,
        hasAddToTrainingError: false,
        isAddingToTraining: true,
    }),


    /**
     * I set loading state on ai-agents-deleting-unanswered event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_DELETING_UNANSWERED]: (
        state: IAiState,
    ): IAiState => ({
        ...state,
        hasDeleteUnansweredError: false,
        isDeletingUnanswered: true,
    }),


    /**
     * I save fetched agent in state.
     *
     * :param state: ai state
     * :param event: ai-agents-fetched-one event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_FETCHED_ONE]:
        (state: IAiState, event: Events.IAiAgentsFetchedOne): IAiState => ({
            ...state,
            agents: {
                ...state.agents,
                [event.aiAgent.id]: {
                    ...state.agents[event.aiAgent.id],
                    ...event.aiAgent,
                },
            },
            hasLoadingOneError: false,
            isLoadingOne: false,
        }),


    /**
     * I set loading state on ai-agents-fetching-one event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_FETCHING_ONE]: (state: IAiState): IAiState => ({
        ...state,
        hasLoadingOneError: false,
        isLoadingOne: true,
    }),


    /**
     * I save implanted agent in state.
     *
     * :param state: ai state
     * :param event: ai-agents-implanted event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_IMPLANTED]:
        (state: IAiState, event: Events.IAiAgentsImplanted): IAiState => ({
            ...state,
            agents: {
                ...state.agents,
                [event._target]: {
                    ...state.agents[event._target],
                    ...event.aiAgent,
                },
            },
            hasImplantError: false,
            isImplanting: false,
        }),


    /**
     * I set loading state on ai-agents-implanting event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_IMPLANTING]: (state: IAiState): IAiState => ({
        ...state,
        hasImplantError: false,
        isImplanting: true,
    }),


    /**
     * I save ai agents listed in state.
     *
     * :param state: ai state
     * :param event: ai-agents-listed event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_LISTED]: (
        state: IAiState,
        event: Events.IAiAgentsListed,
    ): IAiState => ({
        ...state,
        agents: event.byId,
        hasError: false,
        isLoading: false,
    }),


    /**
     * I set loading state on ai-agents-listing event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_LISTING]: (state: IAiState): IAiState => ({
        ...state,
        hasError: false,
        isLoading: true,
    }),


    /**
     * I set error state on ai-agents-not-added-to-training event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_NOT_ADDED_TO_TRAINING]: (
        state: IAiState,
    ): IAiState => ({
        ...state,
        hasAddToTrainingError: true,
        isAddingToTraining: false,
    }),


    /**
     * I set error state on ai-agents-not-fetched-one event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_NOT_FETCHED_ONE]: (state: IAiState): IAiState => ({
        ...state,
        hasLoadingOneError: true,
        isLoadingOne: false,
    }),


    /**
     * I set error state on ai-agents-not-implanted event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_NOT_IMPLANTED]: (state: IAiState): IAiState => ({
        ...state,
        hasImplantError: true,
        isImplanting: false,
    }),


    /**
     * I set error state on ai-agents-not-listed event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_NOT_LISTED]: (state: IAiState): IAiState => ({
        ...state,
        hasError: true,
        isLoading: false,
    }),


    /**
     * I set error state on ai-agents-not-updated event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_NOT_UPDATED]: (state: IAiState): IAiState => ({
        ...state,
        hasUpdateError: true,
        isUpdating: false,
    }),


    /**
     * I update state on ai-agents-unanswered-deleted event.
     *
     * :param state: ai state
     * :param event: ai-agents-updated event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_UNANSWERED_DELETED]: (
        state: IAiState,
        event: Events.IAiAgentsUnansweredDeleted,
    ): IAiState => ({
        ...state,
        agents: {
            ...state.agents,
            [event._target]: {
                ...state.agents[event._target],
                unanswered: state.agents[event._target].unanswered
                    .filter(message => message.id !== event.messageId),
            },
        },
        hasDeleteUnansweredError: false,
        isDeletingUnanswered: false,
    }),


    /**
     * I set error state on ai-agents-unanswered-not-deleted event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_UNANSWERED_NOT_DELETED]: (
        state: IAiState,
    ): IAiState => ({
        ...state,
        hasDeleteUnansweredError: true,
        isDeletingUnanswered: false,
    }),


    /**
     * I save updated agent on ai-agents-updated event.
     *
     * :param state: ai state
     * :param event: ai-agents-updated event
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_UPDATED]: (
        state: IAiState,
        event: Events.IAiAgentsUpdated,
    ): IAiState => ({
        ...state,
        agents: {
            ...state.agents,
            [event._target]: event.aiAgent,
        },
        hasUpdateError: false,
        isUpdating: false,
    }),


    /**
     * I set loading state on ai-agents-updating event.
     *
     * :param state: ai state
     *
     * :returns: ai state
     */
    [Events.types.AI_AGENTS_UPDATING]: (state: IAiState): IAiState => ({
        ...state,
        hasUpdateError: false,
        isUpdating: true,
    }),
};

// AI reducer
const reducer = Store.createReducer(initialState, actionsMap);


/**
 * EXPORTS
 */
export default reducer;

